import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { MediaImage } from "../MediaImage";
import { useSiteMedia } from "../../lib/siteMedia";
import { TextBody } from "../info/TextBody";
import { ACTIVITIES, COMPANY_FACTS, PRINCIPLES, PRODUCT_LINKS } from "../../data/about";

export const CompanyFacts = () => (
  <dl className="card divide-y divide-slate-100" data-testid="about-company-facts">
    {COMPANY_FACTS.map(([k, v]) => (
      <div key={k} className="grid gap-1 px-5 py-3.5 sm:grid-cols-[150px_1fr] sm:gap-4">
        <dt className="text-[13.5px] font-semibold text-slate-500">{k}</dt>
        <dd className="text-[15px] text-ink">{v}</dd>
      </div>
    ))}
  </dl>
);

export const AboutWho = ({ block }) => (
  <div className="grid gap-10 lg:grid-cols-12 lg:gap-14" data-testid="about-who">
    <div className="lg:col-span-7"><TextBody text={block.body} /></div>
    <div className="lg:col-span-5"><CompanyFacts /></div>
  </div>
);

export const AboutActivities = () => (
  <div className="grid gap-8 lg:grid-cols-12 lg:gap-14" data-testid="about-activities">
    <div className="lg:col-span-7">
      <ul className="grid gap-3 sm:grid-cols-2">
        {ACTIVITIES.map((t) => (
          <li key={t} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-[15px] text-ink">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-maas-soft"><Check className="h-4 w-4 text-maas" aria-hidden="true" /></span>{t}
          </li>
        ))}
      </ul>
      <p className="mt-5 text-[14.5px] leading-relaxed text-slate-600" data-testid="about-no-glass-only">Wij leveren altijd complete nieuwe kozijnen, puien en deuren. Alleen het glas vervangen in bestaande kozijnen doen wij niet.</p>
    </div>
    <ul className="grid content-start gap-3 lg:col-span-5">
      {PRODUCT_LINKS.map((p) => (
        <li key={p.href}>
          <Link to={p.href} className="card card-hover group flex items-center justify-between gap-4 p-4 sm:p-5" data-testid={`about-product-${p.href.slice(1)}`}>
            <span><span className="block text-[16px] font-semibold text-ink group-hover:text-maas">{p.title}</span><span className="text-[14px] text-slate-500">{p.text}</span></span>
            <ArrowRight className="h-4 w-4 shrink-0 text-maas transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Photo = ({ media, mediaKey, caption, className }) => {
  const m = media(mediaKey);
  return (
    <figure className={`min-w-0 ${className}`}>
      <div className="frame h-full"><MediaImage media={m} sizes="(min-width: 1024px) 560px, calc(100vw - 32px)" className="h-full min-h-[220px] rounded-[14px]" testId={`about-photo-${mediaKey}`} /></div>
      {m.custom && !m.sfeerbeeld && <figcaption className="mt-2 text-[13px] text-slate-500">{caption}</figcaption>}
    </figure>
  );
};

// Tekst over vakmanschap met vaste fotoplekken (Beheer → Afbeeldingen → Over ons).
export const AboutCraft = ({ block }) => {
  const media = useSiteMedia();
  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" data-testid="about-craft">
      <div className="min-w-0 lg:col-span-5">
        <TextBody text={block.body} />
        <div className="mt-6 flex flex-col gap-3 text-[15px] font-semibold">
          <Link to="/montage" className="inline-flex items-center gap-1.5 text-maas hover:underline" data-testid="about-craft-montage-link">Zo verloopt de montage <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
          <Link to="/werkwijze" className="inline-flex items-center gap-1.5 text-maas hover:underline" data-testid="about-craft-werkwijze-link">Onze werkwijze van aanvraag tot oplevering <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
        </div>
      </div>
      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
        <Photo media={media} mediaKey="ov_hoofdmonteur" caption="Onze hoofdmonteur aan het werk" className="sm:col-span-2 aspect-[16/9]" />
        <Photo media={media} mediaKey="ov_montageploeg" caption="Montage door onze vakmensen" className="aspect-[4/3]" />
        <Photo media={media} mediaKey="ov_werk" caption="Een door ons afgewerkt project" className="aspect-[4/3]" />
      </div>
    </div>
  );
};

export const AboutPrinciples = () => (
  <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-principles">
    {PRINCIPLES.map(({ icon: Icon, title, text }) => (
      <li key={title} className="card card-hover p-6">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-maas-soft text-maas"><Icon className="h-5 w-5" aria-hidden="true" /></span>
        <h3 className="mt-4 text-[16.5px] font-semibold text-ink">{title}</h3>
        <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">{text}</p>
      </li>
    ))}
  </ul>
);
